import React from 'react'
import { StatsCard } from './stats/StatsCard'
import { BuySellSplit } from './BuySellSplit'
import { Card } from '@/components/ui/card'
import TotalProjectsGraph from './TotalProjectsGraph'
import SimpleBarChart from './SimpleBarChart'
import { AverageAmountCard } from './AverageAmountCard'
import { AssetsOnDiscountCard } from './AssetsOnDiscountCard'
import { TotalOrdersCard } from './TotalOrdersCard'
import { TotalValueCard } from './TotalValueCard'
import { formatCompactNumber, formatMoney } from '@/utils'
import { OrderbookStatsResponse } from '@/types/otc'
import { formatChartData } from './DashboardStats/DashboardStatsBase'
import inboxIcon from '@/assets/dashboard/inbox.svg'
import coinIcon from '@/assets/dashboard/coin.svg'
import tagIcon from '@/assets/dashboard/tag.svg'

interface OTCMetricsCardsProps {
  stats: OrderbookStatsResponse | null
  loading?: boolean
}

/**
 * OTC Metrics Cards Component
 * Displays 6 metric cards in 2 rows of 3 cards each as shown in the design
 */
export const OTCMetricsCards: React.FC<OTCMetricsCardsProps> = ({
  stats,
  loading = false
}) => {
  // Calculate metrics
  const totalOrders = (stats?.buy_orders || 0) + (stats?.sell_orders || 0)
  const totalValue = stats?.total_orderbook_value || 0
  const buyOrders = stats?.buy_orders || 0
  const sellOrders = stats?.sell_orders || 0
  const averageAmount = totalOrders > 0 ? totalValue / totalOrders : 0
  const assetsOnDiscount = stats?.assets_on_discount || 0
  const totalProjects = stats?.total_projects || 0

  // Calculate buy/sell percentages
  const totalOrdersForSplit = buyOrders + sellOrders
  const buyPercentage = totalOrdersForSplit > 0 
    ? Math.round((buyOrders / totalOrdersForSplit) * 100) 
    : 0
  const sellPercentage = 100 - buyPercentage

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* 1. Total Orders */}
      <TotalOrdersCard totalOrders={totalOrders} loading={loading} />

      {/* 2. Total Value */}
      <TotalValueCard totalValue={totalValue} loading={loading} monthlyAvg={stats?.orderbook_monthly_avg} />

      {/* 3. Buy/Sell Split */}
      <Card variant="default" padding="none" className="bg-white dark:bg-[#1c1c1c] relative h-[100px] pt-2 pb-2 pl-6 pr-7">
        <BuySellSplit 
          buyCount={buyOrders}
          sellCount={sellOrders}
          buyPercentage={buyPercentage}
          sellPercentage={sellPercentage}
        />
      </Card>

      {/* 4. Average Amount */}
      <AverageAmountCard averageAmount={averageAmount} loading={loading} />

      {/* 5. Assets on Discount */}
      <AssetsOnDiscountCard assetsOnDiscount={assetsOnDiscount} loading={loading} />

      {/* 6. Total Projects */}
      <StatsCard
        icon={inboxIcon}
        iconAlt="Total Projects"
        label="Total Projects"
        value={formatCompactNumber(totalProjects)}
        loading={loading}
        chart={
          <TotalProjectsGraph 
            data={formatChartData(stats?.total_projects_monthly_avg, 3)}
          />
        }
      />
    </div>
  )
}
